/**
 * Usage:
 *   require('./quality-selector');
 *   videojs('my-video', {
 *       plugins: {
 *           qualitySelector: {
 *               currentQuality: 'Auto',
 *               qualities: ['1080p', '720p', '480p', 'Auto'], // optional
 *           },
 *       },
 *   });
 */

const Component = videojs.getComponent('Component');
const MenuButton = videojs.getComponent('MenuButton');
const MenuItem = videojs.getComponent('MenuItem');

class QualityMenuItem extends MenuItem {
    /**
     * @param {object} player
     * @param {object} options
     * @param {string} options.label
     */
    constructor(player, options) {
        super(player, {
            label: options.label,
            selectable: true,
        });
        this.updateSelected();
        this.player_.on('qualitychange', () => this.updateSelected());
    }

    buildCSSClass() {
        return `vjs-quality-selector-item-label ${super.buildCSSClass()}`;
    }

    handleClick(ev) {
        this.player_.currentQuality(this.options_.label);
    }

    updateSelected() {
        this.selected(this.player_.currentQuality() === this.options_.label);
    }
}

class QualitySelectorMenuButton extends MenuButton {
    /**
     * @param {object}   player
     * @param {object}   options
     * @param {string[]} options.qualities
     */
    constructor(player, options) {
        super(player, options);

        this.labelEl_ = document.createElement('span');
        this.labelEl_.classList.add('vjs-quality-selector-menu-label');
        this.labelEl_.innerHTML = '';
        this.el().appendChild(this.labelEl_);

        this.updateLabel();
        this.player_.on('qualitychange', () => this.updateLabel());
    }

    createItems() {
        return this.options_.qualities.map(qo => {
            return new QualityMenuItem(this.player_, { label: qo });
        });
    }

    buildCSSClass() {
        return `vjs-quality-selector ${super.buildCSSClass()}`;
    }

    updateLabel() {
        this.labelEl_.innerHTML = this.player_.currentQuality();
    }
}

class Plugin extends Component {
    /**
     * @param {object}   player
     * @param {object}   [options]
     * @param {string[]} [options.qualities=['1080p', '720p', '480p', 'Auto']]
     * @param {string}   [options.defaultQuality='Auto']
     */
    constructor(player, options) {
        super(player, Object.assign({
            qualities: [
                '1080p',
                '720p',
                '480p',
                'Auto',
            ],
            defaultQuality: 'Auto',
        }, options));
        this.currentQuality_ = this.options_.defaultQuality;
        this.init();
    }

    init(options) {
        if (!options) {
            options = this.options_;
        } else {
            this.options_ = options;
        }
        this.deinit();
        this.player_.currentQuality = q => this.currentQuality(q);
        this.menuButton_ = new QualitySelectorMenuButton(this.player_, {
            qualities: options.qualities,
        });
        this.player_.ready(() => {
            const ctrlBar = this.player_.getChild('controlBar');
            ctrlBar.addChild(this.menuButton_);
        });
    }

    deinit() {
        if (this.menuButton_) {
            this.player_.ready(() => {
                const ctrlBar = this.player_.getChild('controlBar');
                ctrlBar.removeChild(this.menuButton_);
            });
        }
    }

    currentQuality(quality) {
        if (quality) {
            const matchingQuality = this.options_.qualities.find(q => {
                return quality.toLowerCase() === q.toLowerCase();
            });
            if (!matchingQuality) {
                throw new Error('unknown quality');
            }
            this.currentQuality_ = matchingQuality;
            this.player_.trigger('qualitychange');
        }
        return this.currentQuality_ || this.options_.defaultQuality;
    }
}

function plugin(options) {
    const player = this;
    if (player.qualitySelectorPlugin_) {
        player.qualitySelectorPlugin_.init(options);
    } else {
        player.qualitySelectorPlugin_ = new Plugin(player, options);
    }
}

const registerPlugin = videojs.registerPlugin || videojs.plugin;
registerPlugin('qualitySelector', plugin);

export default plugin;



// WEBPACK FOOTER //
// ./src/components/reusable/poly-video/quality-selector.js